import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Lightbulb, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const values = [
  {
    icon: Target,
    title: 'Data-Driven',
    description: 'Every decision is backed by market research and analytics. We track KPIs meticulously to optimize your campaigns.',
  },
  {
    icon: Lightbulb,
    title: 'Creative Excellence',
    description: 'We blend data insights with creative storytelling to create content that resonates and converts.',
  },
  {
    icon: Users,
    title: 'Partnership Focused',
    description: 'We work as an extension of your team, maintaining open communication and collaborative relationships.',
  },
  {
    icon: TrendingUp,
    title: 'Results Obsessed',
    description: 'Our success is measured by your growth. We focus on tangible outcomes and sustained success.',
  },
];

const platforms = [
  { name: 'TikTok', color: 'bg-forest' },
  { name: 'Instagram', color: 'bg-gold' },
  { name: 'Facebook', color: 'bg-forest' },
  { name: 'LinkedIn', color: 'bg-gold' },
  { name: 'Twitter/X', color: 'bg-forest' },
  { name: 'Lemon8', color: 'bg-gold' },
];

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="about-hero">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              Powering Your Online Presence with{' '}
              <span className="text-gold">Data-Driven Strategies</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              We're a dynamic digital marketing agency with a strong focus on data and market research. 
              We craft tailored strategies that drive results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-cream" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1606146350382-64153930ba46?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="Warner Digital workspace"
                  className="rounded-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-gold rounded-2xl p-6 hidden md:block">
                  <p className="text-4xl font-bold text-forest-dark">5★</p>
                  <p className="text-forest-dark/70 text-sm">Client Rating</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-forest mb-6">
                A Top-Performing Creator & Strategist
              </h2>
              <div className="space-y-4 text-forest/70 leading-relaxed">
                <p>
                  Warner Digital Media was founded with a clear mission: to bridge the gap between market 
                  research and measurable marketing results. We believe that great marketing isn't about 
                  guessing—it's about understanding.
                </p>
                <p>
                  Our team meticulously tracks KPIs, leveraging valuable insights to optimize your campaigns. 
                  Whether it's increasing brand awareness or driving conversions, we're here to propel your success.
                </p>
                <p>
                  With expertise spanning content creation, audience growth tactics, and strategic planning, 
                  we leverage graphic design, SEO, and cutting-edge social media management tools to enhance 
                  brand visibility and engagement.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-cream-paper" data-testid="about-values">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 bg-forest rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-gold" size={28} />
                </div>
                <h3 className="text-xl font-semibold text-forest mb-3">{value.title}</h3>
                <p className="text-forest/60 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="section-padding bg-forest" data-testid="about-platforms">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Expertise
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-cream mb-4">
              Platforms We Master
            </h2>
            <p className="text-lg text-cream/60 max-w-2xl mx-auto">
              We specialize in creating impactful content and strategies across all major social platforms.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                className={`${platform.color === 'bg-gold' ? 'bg-gold text-forest-dark' : 'bg-cream text-forest'} px-8 py-4 rounded-full font-semibold text-lg`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`platform-${platform.name.toLowerCase()}`}
              >
                {platform.name}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="section-padding bg-cream" data-testid="about-skills">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                Capabilities
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-forest mb-8">
                Skills & Expertise
              </h2>
              <div className="flex flex-wrap gap-3">
                {[
                  'SEO', 'Content Strategy', 'Copywriting', 'Data Analysis',
                  'Digital Marketing', 'Social Media Management', 'Video Content',
                  'Influencer Marketing', 'Email Marketing', 'Graphic Design',
                  'UGC Creation', 'Community Management', 'Campaign Strategy',
                  'Market Research', 'Brand Development'
                ].map((skill, index) => (
                  <span
                    key={skill}
                    className="bg-forest/10 text-forest px-4 py-2 rounded-full text-sm font-medium"
                    data-testid={`skill-${index}`}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-forest-dark rounded-2xl p-10"
            >
              <h3 className="text-2xl font-bold text-cream mb-6">Committed to Quality</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-cream/80">Client Satisfaction</span>
                    <span className="text-gold font-semibold">100%</span>
                  </div>
                  <div className="h-2 bg-cream/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-cream/80">On-Time Delivery</span>
                    <span className="text-gold font-semibold">100%</span>
                  </div>
                  <div className="h-2 bg-cream/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-cream/80">Repeat Clients</span>
                    <span className="text-gold font-semibold">95%</span>
                  </div>
                  <div className="h-2 bg-cream/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full" style={{ width: '95%' }} />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gold" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest-dark mb-6">
              Let's Work Together
            </h2>
            <p className="text-xl text-forest-dark/70 mb-10 max-w-2xl mx-auto">
              Stay ahead of the curve with Warner Digital. Let's maximize your online presence and achieve tangible growth together.
            </p>
            <Link to="/contact" data-testid="about-cta-button">
              <Button className="bg-forest text-cream hover:bg-forest-dark rounded-full px-10 py-6 text-lg font-semibold">
                Start Your Project
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
